import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Heart, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setState("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("success");
          setTimeout(() => navigate("/planner", { replace: true }), 1600);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        setState("error"); return;
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory px-6">
      <div className="bg-white border border-gold/25 rounded-md p-12 max-w-md w-full text-center shadow-[0_20px_50px_rgba(27,42,71,0.10)]" data-testid="payment-success-page">
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src={LOGO_URL} alt="The Ever Grace Group" className="h-12 w-12 rounded-full object-cover" />
          <span className="font-serif text-2xl text-navy"><span className="text-navy">Ever</span> <span className="text-gold">Grace</span></span>
        </div>
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-sage mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-navy mb-2">Confirming your payment…</h1>
            <p className="text-charcoal/70">This will only take a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-sage mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-navy mb-2" data-testid="payment-success-msg">Welcome — you're all set!</h1>
            <p className="text-charcoal/70">Taking you to your legacy…</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-navy mb-2">We couldn't confirm your payment</h1>
            <p className="text-charcoal/70 mb-6">If you were charged, please refresh in a moment. Otherwise try again.</p>
            <Button onClick={() => navigate("/pricing")} data-testid="back-to-pricing-btn"
              className="bg-navy hover:bg-navy/90 text-ivory rounded-full px-8">Back to plans</Button>
          </>
        )}
      </div>
    </div>
  );
}
